import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import timeEntryService from '../../services/timeEntryService'
import employeeMasterService from '../../services/employeeMasterService'
import projectService from '../../services/projectService'

const TimeEntryForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const entryData = location.state?.entryData

  const [formData, setFormData] = useState({
    entryNumber: '',
    employee: '',
    employeeName: '',
    employeeCode: '',
    department: '',
    entryDate: new Date().toISOString().split('T')[0],
    project: '',
    projectCode: '',
    projectName: '',
    activity: '',
    activityType: 'Development',
    taskDescription: '',
    hours: 0,
    minutes: 0,
    isBillable: true,
    billingRate: 0,
    currency: 'INR',
    location: 'Office',
    notes: '',
    status: 'Draft'
  })

  const [employees, setEmployees] = useState([])
  const [projects, setProjects] = useState([])
  const [isLoadingEntryNumber, setIsLoadingEntryNumber] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next entry number (only for new entries)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchEntryNumber = async () => {
        try {
          setIsLoadingEntryNumber(true)
          const response = await timeEntryService.getNextEntryNumber()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              entryNumber: response.data.entryNumber || response.data.formattedValue || 'TE0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching entry number:', error)
          setFormData(prev => ({ ...prev, entryNumber: 'TE0001' }))
        } finally {
          setIsLoadingEntryNumber(false)
        }
      }
      fetchEntryNumber()
    }
  }, [isEditMode, isViewMode])

  // Load employees and projects
  useEffect(() => {
    loadEmployees()
    loadProjects()
  }, [])

  // Load entry data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && entryData?._id) {
      loadEntryData(entryData._id)
    }
  }, [isEditMode, isViewMode, entryData])

  const loadEmployees = async () => {
    try {
      const response = await employeeMasterService.getAllEmployees()
      if (response.success) {
        setEmployees(response.data || [])
      }
    } catch (error) {
      console.error('Error loading employees:', error)
    }
  }

  const loadProjects = async () => {
    try {
      const response = await projectService.getAllProjects({ limit: 1000 })
      if (response.success) {
        setProjects(response.data || [])
      }
    } catch (error) {
      console.error('Error loading projects:', error)
    }
  }

  const loadEntryData = async (id) => {
    try {
      setIsLoading(true)
      const response = await timeEntryService.getTimeEntryById(id)
      if (response.success && response.data) {
        const entry = response.data
        const hours = Math.floor((entry.totalMinutes || 0) / 60)
        const minutes = (entry.totalMinutes || 0) % 60
        
        setFormData({
          entryNumber: entry.entryNumber || '',
          employee: entry.employee?._id || entry.employee || '',
          employeeName: entry.employeeName || entry.employee?.employeeName || '',
          employeeCode: entry.employeeCode || entry.employee?.employeeCode || '',
          department: entry.department || entry.employee?.department || '',
          entryDate: entry.entryDate ? new Date(entry.entryDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          project: entry.project?._id || entry.project || '',
          projectCode: entry.projectCode || entry.project?.projectCode || '',
          projectName: entry.projectName || entry.project?.projectName || '',
          activity: entry.activity || '',
          activityType: entry.activityType || 'Development',
          taskDescription: entry.taskDescription || '',
          hours: hours,
          minutes: minutes,
          isBillable: entry.isBillable !== undefined ? entry.isBillable : true,
          billingRate: entry.billingRate || 0,
          currency: entry.currency || 'INR',
          location: entry.location || 'Office',
          notes: entry.notes || '',
          status: entry.status || 'Draft'
        })
      }
    } catch (error) {
      console.error('Error loading time entry:', error)
      toast.error('Failed to load time entry data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name === 'employee') {
      const selectedEmployee = employees.find(emp => emp._id === value)
      setFormData(prev => ({
        ...prev,
        employee: value,
        employeeName: selectedEmployee?.employeeName || '',
        employeeCode: selectedEmployee?.employeeCode || '',
        department: selectedEmployee?.department || ''
      }))
    } else if (name === 'project') {
      const selectedProject = projects.find(proj => proj._id === value)
      setFormData(prev => ({
        ...prev,
        project: value,
        projectCode: selectedProject?.projectNumber || '',
        projectName: selectedProject?.projectName || ''
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.employee || !formData.project) {
        toast.error('Please select employee and project')
        return
      }
      if (formData.hours === 0 && formData.minutes === 0) {
        toast.error('Please enter time (hours or minutes)')
        return
      }
      if (formData.hours < 0 || formData.hours > 24) {
        toast.error('Hours must be between 0 and 24')
        return
      }
      if (formData.minutes < 0 || formData.minutes > 59) {
        toast.error('Minutes must be between 0 and 59')
        return
      }

      setIsLoading(true)
      
      if (isEditMode && entryData?._id) {
        const response = await timeEntryService.updateTimeEntry(entryData._id, formData)
        if (response.success) {
          toast.success('Time entry updated successfully!')
          navigate('/timesheets/time-entry')
        } else {
          toast.error(response.message || 'Failed to update time entry')
        }
      } else {
        const response = await timeEntryService.createTimeEntry(formData)
        if (response.success) {
          toast.success('Time entry created successfully!')
          navigate('/timesheets/time-entry')
        } else {
          toast.error(response.message || 'Failed to create time entry')
        }
      }
    } catch (error) {
      console.error('Error saving time entry:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save time entry')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadEntryData(entryData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        employee: '',
        employeeName: '',
        employeeCode: '',
        department: '',
        project: '',
        projectCode: '',
        projectName: '',
        activity: '',
        activityType: 'Development',
        taskDescription: '',
        hours: 0,
        minutes: 0,
        isBillable: true,
        billingRate: 0,
        location: 'Office',
        notes: '',
        status: 'Draft'
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Timesheets', path: '/timesheets' },
    { label: 'Time Entries', path: '/timesheets/time-entry' },
    { 
      label: isEditMode ? 'Edit Entry' : isViewMode ? 'View Entry' : 'New Entry', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading time entry...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Time Entry' : isViewMode ? 'View Time Entry' : 'New Time Entry'}</h1>
          <p>{isEditMode ? 'Update time entry details' : isViewMode ? 'View time entry details' : 'Create a new time entry'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/timesheets/time-entry')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Time Entry Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Entry Details</h3></div>
          {/* Row 1: Entry Number, Employee, Project, Entry Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="entryNumber" className="form-label required">
                Entry Number
              </label>
              <input
                type="text"
                id="entryNumber"
                name="entryNumber"
                value={isLoadingEntryNumber ? 'Generating...' : formData.entryNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="employee" className="form-label required">
                Employee
              </label>
              <select
                id="employee"
                name="employee"
                value={formData.employee}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              >
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {emp.employeeName} ({emp.employeeCode})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="project" className="form-label required">
                Project
              </label>
              <select
                id="project"
                name="project"
                value={formData.project}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              >
                <option value="">Select Project</option>
                {projects.map(proj => (
                  <option key={proj._id} value={proj._id}>
                    {proj.projectName} ({proj.projectNumber})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="entryDate" className="form-label required">
                Entry Date
              </label>
              <input
                type="date"
                id="entryDate"
                name="entryDate"
                value={formData.entryDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              />
            </div>
          </div>

          {/* Row 2: Activity Type, Activity, Location */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="activityType" className="form-label">
                Activity Type
              </label>
              <select
                id="activityType"
                name="activityType"
                value={formData.activityType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Development">Development</option>
                <option value="Testing">Testing</option>
                <option value="Design">Design</option>
                <option value="Meeting">Meeting</option>
                <option value="Documentation">Documentation</option>
                <option value="Support">Support</option>
                <option value="Training">Training</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="activity" className="form-label">
                Activity
              </label>
              <input
                type="text"
                id="activity"
                name="activity"
                value={formData.activity}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="e.g., Feature Development"
              />
            </div>

            <div className="form-group">
              <label htmlFor="location" className="form-label">
                Location
              </label>
              <select
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Office">Office</option>
                <option value="Remote">Remote</option>
                <option value="Client Site">Client Site</option>
                <option value="Travel">Travel</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Draft">Draft</option>
                <option value="Submitted">Submitted</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Invoiced">Invoiced</option>
              </select>
            </div>
          </div>

          <div className="section-header"><h3>Hours & Billing</h3></div>
          {/* Row 3: Hours, Minutes, Is Billable, Billing Rate */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="hours" className="form-label required">
                Hours
              </label>
              <input
                type="number"
                id="hours"
                name="hours"
                value={formData.hours}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="24"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="minutes" className="form-label required">
                Minutes
              </label>
              <input
                type="number"
                id="minutes"
                name="minutes"
                value={formData.minutes}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="59"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="isBillable" className="form-label">
                Billable
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="isBillable"
                  name="isBillable"
                  checked={formData.isBillable}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="isBillable" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Mark as billable
                </label>
              </div>
            </div>

            {formData.isBillable && (
              <div className="form-group">
                <label htmlFor="billingRate" className="form-label">
                  Billing Rate (per hour)
                </label>
                <input
                  type="number"
                  id="billingRate"
                  name="billingRate"
                  value={formData.billingRate}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>
            )}
          </div>

          {/* Row 4: Currency */}
          {formData.isBillable && (
            <div className="grid-row-4">
              <div className="form-group">
                <label htmlFor="currency" className="form-label">
                  Currency
                </label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
          )}

          <div className="section-header"><h3>Description</h3></div>
          {/* Task Description */}
          <div className="form-group">
            <label htmlFor="taskDescription" className="form-label">
              Task Description
            </label>
            <textarea
              id="taskDescription"
              name="taskDescription"
              value={formData.taskDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Describe the work performed..."
            />
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default TimeEntryForm

